/**
 * 통화 오디오 그래프 및 오디오 출력 경로 모듈화 유틸리티
 * 
 * [설계 배경 및 원칙]
 * - 향후 AI 실시간 통역(원격 음성 감쇠 및 통역 음성 오버레이 합성)을 위해
 *   Web Audio API(AudioContext) 그래프 기반의 오디오 라우팅 구조를 제공합니다.
 * - 단, iOS Safari 및 일부 모바일 환경에서의 제약 사항:
 *   (1) iOS Safari는 HTMLMediaElement.volume 설정을 JS로 변경할 수 없음(항상 1 고정).
 *   (2) iOS 기기 무음 스위치(벨소리 끔) 환경에서 Web Audio 출력이 차단될 수 있음.
 *   따라서 실기 검증 전까지는 USE_AUDIO_GRAPH = false (기본 우회 모드)로 안전하게 동작합니다.
 */

// 모드 플래그: true = Web Audio 그래프 출력 모드, false = audio 엘리먼트 직접 재생(우회 모드)
// ※ iOS 무음 스위치 환경에서 Web Audio 출력이 차단될 수 있어, 실기 검증 전까지 false 를 유지한다.
export const USE_AUDIO_GRAPH = false;

// 전역 공유 싱글톤 AudioContext 인스턴스
let sharedAudioCtx = null;
let callActiveChecker = null;
let audioBlockedListeners = new Set();

/**
 * 통화 활성 여부 판정 함수 등록 (useVoiceCall 연동)
 */
export function setCallActiveChecker(fn) {
  callActiveChecker = fn;
}

/**
 * 소리 자동재생 차단(NotAllowedError) 상태 변경 구독
 */
export function onAudioBlockedChange(listener) {
  audioBlockedListeners.add(listener);
  return () => {
    audioBlockedListeners.delete(listener);
  };
}

/**
 * 소리 자동재생 차단 상태 발행
 */
export function setAudioBlocked(blocked) {
  audioBlockedListeners.forEach((fn) => {
    try {
      fn(blocked);
    } catch {}
  });
}

// 가비지 컬렉터(GC)에 의한 무음화를 방지하기 위해 모듈 스코프에 강한 참조(Strong Reference) 보관
let remoteSourceNode = null;
let remoteGainNode = null;

/**
 * 전역 싱글톤 AudioContext 반환
 * 
 * [사유]
 * iOS 에서 사용자 제스처로 확보한 오디오 권한을 두 모듈(callSounds, callAudioGraph)이
 * 중복 획득하지 않도록 하기 위함.
 */
export function getSharedAudioContext() {
  try {
    if (!sharedAudioCtx || sharedAudioCtx.state === 'closed') {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (AudioContextClass) {
        sharedAudioCtx = new AudioContextClass();
        sharedAudioCtx.addEventListener('statechange', () => {
          const state = sharedAudioCtx?.state;
          if (callActiveChecker && callActiveChecker()) {
            if (state === 'suspended' || state === 'interrupted') {
              console.log(`[소리 회복] AudioContext 상태 ${state} 감지 → resume() 시도`);
              sharedAudioCtx.resume().catch((err) => {
                console.warn('[소리 회복] resume() 실패 (무시):', err);
              });
            }
          }
        });
      }
    }
    if (sharedAudioCtx && sharedAudioCtx.state === 'suspended') {
      sharedAudioCtx.resume().catch(() => {});
    }
    return sharedAudioCtx;
  } catch (e) {
    console.warn('[callAudioGraph] AudioContext 초기화 실패 (무시):', e);
    return null;
  }
}

/**
 * 사용자 제스처 동기 핸들러 스택에서 AudioContext 사전 생성 및 활성화
 * (발신 버튼 클릭 및 수신 '받기' 버튼 클릭 시점 동기 호출)
 */
export function initOrResume() {
  try {
    const ctx = getSharedAudioContext();
    if (ctx && ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }
    return ctx;
  } catch (e) {
    console.warn('[callAudioGraph] initOrResume 예외 (무시):', e);
    return null;
  }
}

/**
 * 원격 WebRTC 미디어 스트림을 오디오 출력 파이프라인에 연결
 * 
 * @param {MediaStream} remoteStream - WebRTC 수신 원격 오디오 스트림
 * @param {HTMLAudioElement} audioElement - VoiceCallModal의 숨김 audio 엘리먼트
 */
export function attachRemoteStream(remoteStream, audioElement) {
  if (!remoteStream || !audioElement) return;

  // 1. 공통: 브라우저 WebRTC 디코딩 파이프라인 활성화(Keep-alive)를 위해 srcObject 연결 및 play()
  audioElement.srcObject = remoteStream;
  audioElement.play().catch((e) => {
    console.warn('[callAudioGraph] 오디오 엘리먼트 재생 실패:', e);
    if (e?.name === 'NotAllowedError') {
      setAudioBlocked(true);
    }
  });

  // 2. 우회 모드 (USE_AUDIO_GRAPH === false): 기존과 동일하게 audio 엘리먼트가 직접 소리를 냄
  if (!USE_AUDIO_GRAPH) {
    audioElement.muted = false;
    return;
  }

  // 3. 그래프 모드 (USE_AUDIO_GRAPH === true): audio 엘리먼트는 무음 keep-alive로만 사용하고 AudioContext가 출력
  // ※ iOS 는 volume 설정이 무시되므로 muted 가 실질 차단 수단이며, volume=0 은 데스크톱 브라우저용 보조 수단으로 병행한다.
  audioElement.muted = true;
  try {
    audioElement.volume = 0;
  } catch (e) {}

  try {
    const ctx = getSharedAudioContext();
    if (!ctx) return;

    // 기존 연결 노드가 있다면 정리
    if (remoteSourceNode) {
      try { remoteSourceNode.disconnect(); } catch (e) {}
    }
    if (remoteGainNode) {
      try { remoteGainNode.disconnect(); } catch (e) {}
    }

    // MediaStreamSource -> GainNode -> Destination 그래프 구성
    remoteSourceNode = ctx.createMediaStreamSource(remoteStream);
    remoteGainNode = ctx.createGain();
    remoteGainNode.gain.setValueAtTime(1.0, ctx.currentTime);

    remoteSourceNode.connect(remoteGainNode);
    remoteGainNode.connect(ctx.destination);
  } catch (err) {
    console.error('[callAudioGraph] AudioContext 그래프 구성 실패 (폴백: audio 직접 재생):', err);
    // 그래프 구성 실패 시 오디오 엘리먼트 직접 재생으로 안전하게 폴백
    audioElement.muted = false;
    try {
      audioElement.volume = 1.0;
    } catch (e) {}
  }
}

/**
 * 원격 음성 볼륨 조절 (향후 AI 실시간 통역 시 덕킹 0.2~0.3 감쇠용 인터페이스)
 * 
 * @param {number} value - 0.0 ~ 1.0 사이의 볼륨 배율
 */
export function setRemoteVolume(value) {
  if (!remoteGainNode || typeof value !== 'number') return;
  const clamped = Math.max(0.0, Math.min(1.0, value));
  try {
    const ctx = getSharedAudioContext();
    const currentTime = ctx ? ctx.currentTime : 0;
    remoteGainNode.gain.setValueAtTime(clamped, currentTime);
  } catch (e) {
    console.warn('[callAudioGraph] setRemoteVolume 실패:', e);
  }
}

/**
 * 통화 종료 시 오디오 노드 해제 및 리소스 클린업
 * 
 * @param {HTMLAudioElement} [audioElement] - 초기화할 audio 엘리먼트
 */
export function cleanup(audioElement) {
  // 1. Web Audio 노드 연결 해제
  if (remoteSourceNode) {
    try {
      remoteSourceNode.disconnect();
    } catch (e) {}
    remoteSourceNode = null;
  }

  if (remoteGainNode) {
    try {
      remoteGainNode.disconnect();
    } catch (e) {}
    remoteGainNode = null;
  }

  // 2. audio 엘리먼트 원상 복구
  if (audioElement) {
    audioElement.srcObject = null;
    audioElement.muted = false;
    try {
      audioElement.volume = 1.0;
    } catch (e) {}
  }
  setAudioBlocked(false);

  // 3. AudioContext 생명주기 관리
  // suspend() 된 컨텍스트는 사용자 제스처 없는 비동기 수신 콜백에서
  // resume() 이 차단되어, 다음 수신 전화의 벨소리가 울리지 않는다.
  // 따라서 컨텍스트는 running 상태로 유지하고 노드만 정리한다.
}

/**
 * 통화 소리 출력 장치 변경의 단일 출구. 향후 통역 믹서 출력도 이 함수를 거친다.
 */
export async function setOutputDevice(audioElement, deviceId) {
  if (!audioElement || typeof audioElement.setSinkId !== 'function') {
    throw new Error('setSinkId 미지원');
  }
  if (import.meta.env.DEV) console.info('[소리 출구] 출력 장치 변경:', deviceId);
  await audioElement.setSinkId(deviceId);
}

